import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useBrandRegistrationCopy } from '../../../brands/registrationCopy';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import { NumberTextFieldNoWheelRHF } from './NumberTextFieldNoWheelRHF';
import BatchedTicketLinksPanel from './BatchedTicketLinksPanel';
import type { EventFormData } from '../EventFormContainer';
import { eventService } from '../../../services/eventService';
import {
  PHASE_ADVANCE_OPTIONS,
  addDays,
  defaultPhaseDates,
  diffDays,
  formatPhaseDateShort,
  getAdvanceHint,
  isEventDrivenAdvance,
  type PhaseAdvanceWhen,
} from './phasedPhaseUtils';

interface PhasedTicketsFormProps {
  eventId?: string;
  onReload?: () => void;
}

function parsePhaseDate(value: unknown): Date | undefined {
  if (!value) return undefined;
  const d = value instanceof Date ? value : new Date(value as string);
  return Number.isNaN(d.getTime()) ? undefined : d;
}

function mergeCatalogDates(
  catalog: { ticketTypes?: EventFormData['ticketTypes']; salesPhases?: EventFormData['salesPhases'] }
): { ticketTypes?: EventFormData['ticketTypes']; salesPhases?: EventFormData['salesPhases'] } {
  return {
    ticketTypes: catalog.ticketTypes?.map((type) => ({
      ...type,
      batches: type.batches?.map((batch) => ({
        ...batch,
        startSaleDate: parsePhaseDate(batch.startSaleDate),
        endSaleDate: parsePhaseDate(batch.endSaleDate),
      })),
    })),
    salesPhases: catalog.salesPhases?.map((phase) => ({
      ...phase,
      startSaleDate: parsePhaseDate(phase.startSaleDate),
      endSaleDate: parsePhaseDate(phase.endSaleDate),
    })),
  };
}

const PhasedTicketsForm: React.FC<PhasedTicketsFormProps> = ({ eventId, onReload }) => {
  const { control, watch, setValue, getValues } = useFormContext<EventFormData>();
  const t = useBrandRegistrationCopy();
  const slug = watch('slug') || '';
  const [selectedPhaseIndex, setSelectedPhaseIndex] = useState(0);
  const [mainTab, setMainTab] = useState<'phases' | 'links'>('phases');
  const [activating, setActivating] = useState(false);
  const [savingPhaseDates, setSavingPhaseDates] = useState(false);
  const [phaseSaveError, setPhaseSaveError] = useState<string | null>(null);
  const [publicActivePhaseId, setPublicActivePhaseId] = useState<string | null>(null);
  const manualEndEditRef = useRef<Set<number>>(new Set());
  const dirtyPhasesRef = useRef<Set<number>>(new Set());
  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const persistGenRef = useRef(0);
  const persistInFlightRef = useRef(false);
  const pendingPersistRef = useRef(false);

  const { fields: typeFields, append: appendType, remove: removeType } = useFieldArray({
    control,
    name: 'ticketTypes',
  });

  const { fields: phaseFields, append: appendPhase, remove: removePhase } = useFieldArray({
    control,
    name: 'salesPhases',
  });

  const ticketTypes = watch('ticketTypes') || [];
  const salesPhases = watch('salesPhases') || [];

  const getOfferIndex = (phaseIndex: number, typeIndex: number): number => {
    const offers = watch(`salesPhases.${phaseIndex}.offers`) || [];
    const typeId = watch(`ticketTypes.${typeIndex}._id`);
    const typeName = watch(`ticketTypes.${typeIndex}.name`);
    return offers.findIndex(
      (o: { ticketTypeId?: string; ticketTypeKey?: string }) =>
        (typeId && o.ticketTypeId === typeId) ||
        o.ticketTypeKey === typeName ||
        o.ticketTypeKey === typeId
    );
  };

  const ensureOffer = (phaseIndex: number, typeIndex: number) => {
    const offers = [...(watch(`salesPhases.${phaseIndex}.offers`) || [])];
    const idx = getOfferIndex(phaseIndex, typeIndex);
    if (idx >= 0) return idx;
    const typeId = watch(`ticketTypes.${typeIndex}._id`);
    const typeName = watch(`ticketTypes.${typeIndex}.name`);
    offers.push({
      ticketTypeId: typeId,
      ticketTypeKey: typeName,
      price: 0,
      quantity: 100,
      isActive: true,
    });
    setValue(`salesPhases.${phaseIndex}.offers`, offers, { shouldDirty: true });
    return offers.length - 1;
  };

  const getEffectivePhaseStart = (phaseIndex: number): Date => {
    const advanceWhen = watch(`salesPhases.${phaseIndex}.advanceWhen`) as PhaseAdvanceWhen | undefined;

    if (phaseIndex > 0 && isEventDrivenAdvance(advanceWhen)) {
      const prevEnd = watch(`salesPhases.${phaseIndex - 1}.endSaleDate`) as Date | undefined;
      if (prevEnd) return new Date(prevEnd);
    }

    const direct = watch(`salesPhases.${phaseIndex}.startSaleDate`) as Date | undefined;
    if (direct) return new Date(direct);

    if (phaseIndex > 0) {
      const prevEnd = watch(`salesPhases.${phaseIndex - 1}.endSaleDate`) as Date | undefined;
      if (prevEnd) return new Date(prevEnd);
    }

    return new Date();
  };

  /** Propaga fim do lote N → início do lote N+1 quando abertura é por data. */
  const propagateEndToNextStart = (phaseIndex: number, endDate?: Date | null) => {
    const nextIndex = phaseIndex + 1;
    if (nextIndex >= phaseFields.length || !endDate) return;
    const nextAdvance = watch(`salesPhases.${nextIndex}.advanceWhen`) as PhaseAdvanceWhen | undefined;
    if ((nextAdvance || 'date') === 'date') {
      setValue(`salesPhases.${nextIndex}.startSaleDate`, new Date(endDate), { shouldDirty: true });
    }
  };

  const setPhaseEndFromDuration = (phaseIndex: number, durationDays: number) => {
    if (durationDays < 1) return;
    manualEndEditRef.current.delete(phaseIndex);
    dirtyPhasesRef.current.add(phaseIndex);
    const start = getEffectivePhaseStart(phaseIndex);
    const end = addDays(start, durationDays);
    setValue(`salesPhases.${phaseIndex}.endSaleDate`, end, { shouldDirty: true, shouldTouch: true });
    propagateEndToNextStart(phaseIndex, end);
  };

  const applyCatalogToForm = useCallback(
    (catalog: { ticketTypes?: EventFormData['ticketTypes']; salesPhases?: EventFormData['salesPhases'] }) => {
      const parsed = mergeCatalogDates(catalog);
      if (parsed.ticketTypes) {
        setValue('ticketTypes', parsed.ticketTypes, { shouldDirty: false });
      }
      if (parsed.salesPhases) {
        setValue('salesPhases', parsed.salesPhases, { shouldDirty: false });
      }
    },
    [setValue]
  );

  const refreshPublicActivePhase = useCallback(async () => {
    if (!eventId) return;
    try {
      const res = await eventService.getActiveSalesPhase(eventId);
      const id = (res.data as { _id?: string } | null | undefined)?._id;
      setPublicActivePhaseId(id ? String(id) : null);
    } catch {
      setPublicActivePhaseId(null);
    }
  }, [eventId]);

  const persistPhaseCatalog = useCallback(async () => {
    if (!eventId) return;
    if (persistInFlightRef.current) {
      pendingPersistRef.current = true;
      return;
    }

    if (persistTimerRef.current) {
      clearTimeout(persistTimerRef.current);
      persistTimerRef.current = null;
    }

    const gen = ++persistGenRef.current;
    persistInFlightRef.current = true;
    setSavingPhaseDates(true);
    setPhaseSaveError(null);

    try {
      const values = getValues();
      await eventService.saveSalesPhases(eventId, {
        ticketTypes: values.ticketTypes,
        salesPhases: values.salesPhases,
      });

      if (gen !== persistGenRef.current) return;

      dirtyPhasesRef.current.clear();
      await refreshPublicActivePhase();
    } catch (e: unknown) {
      if (gen === persistGenRef.current) {
        setPhaseSaveError(e instanceof Error ? e.message : 'Erro ao salvar datas do lote');
      }
    } finally {
      if (gen === persistGenRef.current) {
        persistInFlightRef.current = false;
        setSavingPhaseDates(false);
        if (pendingPersistRef.current) {
          pendingPersistRef.current = false;
          queueMicrotask(() => void persistPhaseCatalog());
        }
      }
    }
  }, [eventId, getValues, refreshPublicActivePhase]);

  const flushPersist = useCallback(() => {
    queueMicrotask(() => void persistPhaseCatalog());
  }, [persistPhaseCatalog]);

  const schedulePersist = useCallback(() => {
    if (!eventId) return;
    if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    persistTimerRef.current = setTimeout(() => {
      void persistPhaseCatalog();
    }, 600);
  }, [eventId, persistPhaseCatalog]);

  useEffect(() => () => {
    if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
  }, []);

  useEffect(() => {
    void refreshPublicActivePhase();
  }, [refreshPublicActivePhase]);

  /** Lotes por esgotar/expirar não devem guardar início fixo — distorce a duração. */
  useEffect(() => {
    salesPhases.forEach((phase: { advanceWhen?: PhaseAdvanceWhen; startSaleDate?: Date }, pi: number) => {
      if (pi > 0 && isEventDrivenAdvance(phase.advanceWhen) && phase.startSaleDate) {
        setValue(`salesPhases.${pi}.startSaleDate`, undefined, { shouldDirty: false });
      }
    });
  }, [eventId, salesPhases.length, setValue]); // eslint-disable-line react-hooks/exhaustive-deps

  const addType = () => {
    const idx = typeFields.length;
    appendType({
      name: `Ingresso ${idx + 1}`,
      description: '',
      sortOrder: idx,
      isActive: true,
      hiddenFromPublicPage: false,
      isRestricted: false,
      allowedEmails: [],
      showPlatformFee: false,
    });
    salesPhases.forEach((_, pi) => {
      const offers = [...(watch(`salesPhases.${pi}.offers`) || [])];
      offers.push({
        ticketTypeKey: `Ingresso ${idx + 1}`,
        price: 0,
        quantity: 100,
      });
      setValue(`salesPhases.${pi}.offers`, offers, { shouldDirty: true });
    });
  };

  const addPhase = () => {
    const idx = phaseFields.length;
    const prevEnd = idx > 0 ? (watch(`salesPhases.${idx - 1}.endSaleDate`) as Date | undefined) : undefined;
    const { start, end } = defaultPhaseDates(prevEnd, 7);
    const advanceWhen: PhaseAdvanceWhen = idx === 0 ? 'date' : 'previous_sold_out';
    appendPhase({
      name: `Lote ${idx + 1}`,
      sortOrder: idx,
      ...(advanceWhen === 'date' ? { startSaleDate: start } : {}),
      endSaleDate: end,
      advanceWhen,
      isActive: true,
      offers: ticketTypes.map((t: { _id?: string; name: string; price?: number; quantity?: number }) => ({
        ticketTypeId: t._id,
        ticketTypeKey: t.name,
        price: t.price ?? 0,
        quantity: t.quantity ?? 100,
      })),
    });
    setSelectedPhaseIndex(idx);
  };

  const handleManualActivate = async (phaseId?: string) => {
    if (!eventId || !phaseId) return;
    setActivating(true);
    try {
      await eventService.activateSalesPhase(eventId, phaseId);
      onReload?.();
    } finally {
      setActivating(false);
    }
  };

  const hiddenCount = ticketTypes.filter((t: { hiddenFromPublicPage?: boolean }) => t.hiddenFromPublicPage).length;

  const selectedAdvance = watch(`salesPhases.${selectedPhaseIndex}.advanceWhen`) as PhaseAdvanceWhen | undefined;
  const isFirstPhase = selectedPhaseIndex === 0;
  const showStartDate = isFirstPhase || selectedAdvance === 'date';
  const eventDriven = !isFirstPhase && isEventDrivenAdvance(selectedAdvance);
  const prevPhaseName = selectedPhaseIndex > 0
    ? (watch(`salesPhases.${selectedPhaseIndex - 1}.name`) as string) || `Lote ${selectedPhaseIndex}`
    : undefined;

  const selectedStart = getEffectivePhaseStart(selectedPhaseIndex);
  const selectedEnd = watch(`salesPhases.${selectedPhaseIndex}.endSaleDate`) as Date | undefined;
  const durationDays =
    selectedStart && selectedEnd ? diffDays(selectedStart, selectedEnd) : 7;

  return (
    <Box>
      <Paper elevation={0} variant="outlined">
        <Tabs
          value={mainTab}
          onChange={(_, v) => setMainTab(v)}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            value="phases"
            icon={<ConfirmationNumberOutlinedIcon fontSize="small" />}
            iconPosition="start"
            label={`Lotes e ingressos (${phaseFields.length} lote${phaseFields.length !== 1 ? 's' : ''})`}
          />
          <Tab
            value="links"
            icon={<LinkOutlinedIcon fontSize="small" />}
            iconPosition="start"
            label={`Links promocionais${hiddenCount ? ` (${hiddenCount})` : ''}`}
          />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {mainTab === 'phases' && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Selecione um lote para configurar preços e datas. A etiqueta{' '}
                <Box
                  component="span"
                  sx={{
                    display: 'inline-block',
                    px: 0.75,
                    py: 0.1,
                    mx: 0.25,
                    borderRadius: 999,
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    letterSpacing: 0.4,
                    verticalAlign: 'middle',
                    bgcolor: 'success.main',
                    color: 'success.contrastText',
                  }}
                >
                  AO VIVO
                </Box>{' '}
                indica o lote visível na página pública.
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 1,
                  mb: 3,
                  p: 1,
                  borderRadius: 2,
                  bgcolor: 'grey.50',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                {phaseFields.map((field, pi) => {
                  const phaseName = watch(`salesPhases.${pi}.name`) || `Lote ${pi + 1}`;
                  const phaseId = watch(`salesPhases.${pi}._id`) as string | undefined;
                  const isPublicActive = !!(phaseId && publicActivePhaseId === String(phaseId));
                  const isSelected = selectedPhaseIndex === pi;

                  return (
                    <Box
                      key={field.id}
                      role="button"
                      tabIndex={0}
                      aria-pressed={isSelected}
                      aria-label={`${phaseName}${isPublicActive ? ', ao vivo na página pública' : ''}`}
                      onClick={() => setSelectedPhaseIndex(pi)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setSelectedPhaseIndex(pi);
                        }
                      }}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.75,
                        pl: 1.75,
                        pr: phaseFields.length > 1 ? 0.25 : 1.75,
                        py: 0.75,
                        minHeight: 40,
                        borderRadius: 1.5,
                        border: '2px solid',
                        borderColor: isSelected ? 'primary.main' : 'grey.300',
                        bgcolor: isSelected ? 'primary.main' : 'background.paper',
                        color: isSelected ? 'primary.contrastText' : 'text.primary',
                        cursor: 'pointer',
                        userSelect: 'none',
                        boxShadow: isSelected ? 1 : 'none',
                        transition: 'border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease',
                        '&:hover': {
                          borderColor: isSelected ? 'primary.main' : 'primary.light',
                          bgcolor: isSelected ? 'primary.dark' : 'background.paper',
                        },
                        '&:focus-visible': {
                          outline: '2px solid',
                          outlineColor: 'primary.main',
                          outlineOffset: 2,
                        },
                      }}
                    >
                      <Typography variant="body2" fontWeight={isSelected ? 600 : 500} lineHeight={1.2}>
                        {phaseName}
                      </Typography>
                      {isPublicActive && (
                        <Box
                          component="span"
                          sx={{
                            px: 0.75,
                            py: 0.15,
                            borderRadius: 999,
                            fontSize: '0.62rem',
                            fontWeight: 700,
                            letterSpacing: 0.35,
                            lineHeight: 1.5,
                            bgcolor: isSelected ? 'rgba(255,255,255,0.22)' : 'success.main',
                            color: isSelected ? 'inherit' : 'success.contrastText',
                          }}
                        >
                          AO VIVO
                        </Box>
                      )}
                      {phaseFields.length > 1 && (
                        <IconButton
                          size="small"
                          aria-label={`Remover ${phaseName}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            removePhase(pi);
                          }}
                          sx={{
                            color: isSelected ? 'inherit' : 'text.secondary',
                            opacity: 0.65,
                            '&:hover': {
                              opacity: 1,
                              bgcolor: isSelected ? 'rgba(255,255,255,0.12)' : 'action.hover',
                            },
                          }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      )}
                    </Box>
                  );
                })}
                <Button
                  size="small"
                  variant="text"
                  startIcon={<AddIcon />}
                  onClick={addPhase}
                  sx={{ fontWeight: 500, color: 'primary.main' }}
                >
                  Adicionar lote
                </Button>
              </Box>

              {phaseSaveError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPhaseSaveError(null)}>
                  {phaseSaveError}
                </Alert>
              )}

              {phaseFields[selectedPhaseIndex] && (
                <Paper
                  key={phaseFields[selectedPhaseIndex].id}
                  variant="outlined"
                  sx={{ p: 2, mb: 3 }}
                >
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Configuração: {watch(`salesPhases.${selectedPhaseIndex}.name`)}
                  </Typography>

                  {!isFirstPhase && (
                    <Controller
                      name={`salesPhases.${selectedPhaseIndex}.advanceWhen`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          select
                          fullWidth
                          size="small"
                          label="Abrir este lote quando"
                          sx={{ mb: 2 }}
                          onChange={(e) => {
                            const mode = e.target.value as PhaseAdvanceWhen;
                            field.onChange(mode);
                            dirtyPhasesRef.current.add(selectedPhaseIndex);
                            if (mode === 'date') {
                              const prevEnd = watch(`salesPhases.${selectedPhaseIndex - 1}.endSaleDate`) as Date | undefined;
                              if (prevEnd) {
                                setValue(`salesPhases.${selectedPhaseIndex}.startSaleDate`, new Date(prevEnd), { shouldDirty: true });
                              }
                            } else {
                              setValue(`salesPhases.${selectedPhaseIndex}.startSaleDate`, undefined, { shouldDirty: true });
                              if (!manualEndEditRef.current.has(selectedPhaseIndex)) {
                                const days = durationDays >= 1 ? durationDays : 7;
                                setPhaseEndFromDuration(selectedPhaseIndex, days);
                              }
                            }
                            schedulePersist();
                          }}
                        >
                          {PHASE_ADVANCE_OPTIONS.map((o) => (
                            <MenuItem key={o.value} value={o.value}>
                              <Box>
                                <Typography variant="body2">{o.label}</Typography>
                                <Typography variant="caption" color="text.secondary">{o.hint}</Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                  )}

                  {eventDriven && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      {getAdvanceHint(selectedAdvance, prevPhaseName)}
                      {' '}Defina apenas <strong>até quando</strong> este lote fica disponível.
                      {prevPhaseName && (
                        <>
                          {' '}O início será após o fim de <strong>{prevPhaseName}</strong>
                          {watch(`salesPhases.${selectedPhaseIndex - 1}.endSaleDate`) && (
                            <> ({formatPhaseDateShort(watch(`salesPhases.${selectedPhaseIndex - 1}.endSaleDate`))})</>
                          )}.
                        </>
                      )}
                    </Alert>
                  )}

                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12} md={showStartDate ? 3 : 4}>
                        <Controller
                          name={`salesPhases.${selectedPhaseIndex}.name`}
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              size="small"
                              label="Nome do lote"
                              onBlur={(e) => {
                                field.onBlur();
                                dirtyPhasesRef.current.add(selectedPhaseIndex);
                                schedulePersist();
                              }}
                              onChange={(e) => {
                                field.onChange(e);
                                dirtyPhasesRef.current.add(selectedPhaseIndex);
                              }}
                            />
                          )}
                        />
                      </Grid>

                      {showStartDate && (
                        <Grid item xs={12} md={3}>
                          <Controller
                            name={`salesPhases.${selectedPhaseIndex}.startSaleDate`}
                            control={control}
                            render={({ field }) => (
                              <DateTimePicker
                                label={isFirstPhase ? 'Início das vendas' : 'Abre em (data)'}
                                value={field.value ?? null}
                                onChange={(d) => {
                                  field.onChange(d);
                                  dirtyPhasesRef.current.add(selectedPhaseIndex);
                                  if (d && !manualEndEditRef.current.has(selectedPhaseIndex)) {
                                    setPhaseEndFromDuration(selectedPhaseIndex, durationDays);
                                  }
                                  schedulePersist();
                                }}
                                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                              />
                            )}
                          />
                        </Grid>
                      )}

                      <Grid item xs={12} md={showStartDate ? 2 : 3}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Duração (dias)"
                          value={durationDays}
                          inputProps={{ min: 1 }}
                          onChange={(e) => {
                            const days = Number(e.target.value);
                            if (days >= 1) {
                              setPhaseEndFromDuration(selectedPhaseIndex, days);
                              schedulePersist();
                            }
                          }}
                          onBlur={() => {
                            if (persistTimerRef.current) {
                              clearTimeout(persistTimerRef.current);
                              persistTimerRef.current = null;
                            }
                            if (dirtyPhasesRef.current.has(selectedPhaseIndex)) {
                              flushPersist();
                            }
                          }}
                          helperText={
                            savingPhaseDates
                              ? 'Salvando…'
                              : 'Altera o fim automaticamente e salva ao sair do campo'
                          }
                        />
                      </Grid>

                      <Grid item xs={12} md={showStartDate ? 4 : 5}>
                        <Controller
                          name={`salesPhases.${selectedPhaseIndex}.endSaleDate`}
                          control={control}
                          render={({ field }) => (
                            <DateTimePicker
                              label="Fim das vendas"
                              value={field.value ?? null}
                              onChange={(d) => {
                                manualEndEditRef.current.add(selectedPhaseIndex);
                                dirtyPhasesRef.current.add(selectedPhaseIndex);
                                field.onChange(d);
                                propagateEndToNextStart(selectedPhaseIndex, d);
                              }}
                              onAccept={(d) => {
                                if (d) {
                                  manualEndEditRef.current.add(selectedPhaseIndex);
                                  dirtyPhasesRef.current.add(selectedPhaseIndex);
                                  setValue(`salesPhases.${selectedPhaseIndex}.endSaleDate`, d, {
                                    shouldDirty: true,
                                    shouldTouch: true,
                                  });
                                  propagateEndToNextStart(selectedPhaseIndex, d);
                                  flushPersist();
                                }
                              }}
                              onClose={() => {
                                if (dirtyPhasesRef.current.has(selectedPhaseIndex)) {
                                  flushPersist();
                                }
                              }}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  size: 'small',
                                  helperText: selectedPhaseIndex < phaseFields.length - 1
                                    ? 'Ao mudar, o próximo lote (por data) encaixa o início aqui'
                                    : undefined,
                                },
                              }}
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                  </LocalizationProvider>

                  {selectedAdvance === 'manual' &&
                    watch(`salesPhases.${selectedPhaseIndex}._id`) &&
                    eventId && (
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={activating}
                        onClick={() =>
                          handleManualActivate(watch(`salesPhases.${selectedPhaseIndex}._id`))
                        }
                        sx={{ mb: 2 }}
                      >
                        Abrir este lote agora
                      </Button>
                    )}

                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Tipos de ingresso neste lote</Typography>
                  {typeFields.map((typeField, ti) => {
                    ensureOffer(selectedPhaseIndex, ti);
                    const offerIdx = getOfferIndex(selectedPhaseIndex, ti);
                    if (offerIdx < 0) return null;
                    const typeName = watch(`ticketTypes.${ti}.name`) || `Tipo ${ti + 1}`;
                    return (
                      <Paper key={typeField.id} variant="outlined" sx={{ p: 2, mb: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography fontWeight={600}>{typeName}</Typography>
                          {typeFields.length > 1 && (
                            <Tooltip title="Remover tipo de ingresso">
                              <IconButton size="small" color="error" onClick={() => removeType(ti)}>
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Controller
                              name={`ticketTypes.${ti}.name`}
                              control={control}
                              render={({ field }) => (
                                <TextField {...field} fullWidth size="small" label="Nome do tipo" />
                              )}
                            />
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <Controller
                              name={`salesPhases.${selectedPhaseIndex}.offers.${offerIdx}.price`}
                              control={control}
                              render={({ field }) => (
                                <NumberTextFieldNoWheelRHF
                                  field={field}
                                  fullWidth
                                  size="small"
                                  type="number"
                                  label="Preço (R$)"
                                  InputProps={{
                                    startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                                  }}
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <Controller
                              name={`salesPhases.${selectedPhaseIndex}.offers.${offerIdx}.quantity`}
                              control={control}
                              render={({ field }) => (
                                <NumberTextFieldNoWheelRHF
                                  field={field}
                                  fullWidth
                                  size="small"
                                  type="number"
                                  label="Vagas"
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Controller
                              name={`ticketTypes.${ti}.description`}
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  fullWidth
                                  size="small"
                                  label="Descrição"
                                  placeholder="Ex: Válido para um dia, inclui material..."
                                  helperText={t('Exibida na página de inscrição para este tipo de ingresso')}
                                  multiline
                                  minRows={2}
                                />
                              )}
                            />
                          </Grid>
                        </Grid>
                      </Paper>
                    );
                  })}

                  <Button variant="outlined" startIcon={<AddIcon />} onClick={addType} sx={{ mt: 1 }}>
                    Adicionar tipo de ingresso
                  </Button>
                </Paper>
              )}
            </>
          )}

          {mainTab === 'links' && <BatchedTicketLinksPanel slug={slug} />}
        </Box>
      </Paper>
    </Box>
  );
};

export default PhasedTicketsForm;
